import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { FiDollarSign, FiExternalLink, FiCheck, FiCopy } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const TipJar = ({ profile, theme }) => {
  const { isWalletConnected } = useAuth();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('ETH');
  const [showPayment, setShowPayment] = useState(false);
  const [copied, setCopied] = useState(false);
  const tipOptions = {
    ETH: [0.01, 0.05, 0.1, 0.5, 1],
    USDC: [5, 10, 25, 50, 100],
    USD: [5, 10, 25, 50, 100]
  };

  const handleQuickTip = (value) => {
    setAmount(value);
    setShowPayment(true);
  };

  const handleCopyAddress = async () => {
    if (profile.wallet_address) {
      await navigator.clipboard.writeText(profile.wallet_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getPaymentUrl = () => {
    if (!profile.wallet_address) return '';
    switch(currency) {
      case 'ETH':
        return `ethereum:${profile.wallet_address}?value=${amount * 1e18}`;
      case 'USDC':
        return `ethereum:${profile.wallet_address}?value=${amount * 1e6}`;
      case 'USD':
        return `https://checkout.stripe.com/pay?customer_email=&amount=${amount * 100}&currency=usd&name=${profile.display_name || profile.username}`;
      default:
        return '';
    }
  };

  if (!profile.wallet_address) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border ${theme.border} ${theme.bg} overflow-hidden`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-white text-xl" />
            </div>
            <h3 className={`font-semibold ${theme.text}`}>Support Creator</h3>
          </div>
        </div>

        <p className={`${theme.subtext} mb-6`}>
          Show your appreciation with a tip! Every contribution helps create more amazing content.
        </p>

        {/* Currency Selector */}
        <div className="flex space-x-2 mb-6">
          {Object.keys(tipOptions).map((curr) => (
            <button
              key={curr}
              onClick={() => setCurrency(curr)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currency === curr
                  ? 'bg-indigo-600 text-white'
                  : `${theme.bg} ${theme.border} border ${theme.text} hover:opacity-80`
              }`}
            >
              {curr}
            </button>
          ))}
        </div>

        {/* Quick Tip Options */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6">
          {tipOptions[currency].map((value) => (
            <button
              key={value}
              onClick={() => handleQuickTip(value)}
              className={`py-3 rounded-lg font-medium transition-all ${
                amount === value
                  ? 'bg-indigo-600 text-white scale-105'
                  : `${theme.bg} ${theme.border} border ${theme.text} hover:scale-105`
              }`}
            >
              {currency === 'USD' ? `$${value}` : value}
            </button>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="mb-6">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Custom amount (${currency})`}
            className={`w-full px-4 py-3 rounded-lg border ${theme.border} ${theme.bg} ${theme.text} focus:ring-2 focus:ring-indigo-500`}
          />
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {amount && (
            <a
              href={getPaymentUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              <SafeIcon icon={FiExternalLink} className="mr-2" />
              Send {currency}
            </a>
          )}
          <button
            onClick={handleCopyAddress}
            className={`inline-flex items-center justify-center px-4 py-3 ${theme.bg} ${theme.border} border ${theme.text} rounded-lg font-medium hover:opacity-80 transition-colors`}
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} className="mr-2" />
            {copied ? 'Copied!' : 'Copy Address'}
          </button>
        </div>

        {/* Wallet Address Display */}
        <div className={`mt-6 p-3 rounded-lg ${theme.bg} ${theme.border} border`}>
          <p className={`text-xs ${theme.subtext} mb-1`}>Wallet Address:</p>
          <p className={`font-mono text-sm ${theme.text} break-all`}>
            {profile.wallet_address}
          </p>
        </div>

        {/* Support Tiers */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className={`font-medium ${theme.text} mb-3`}>Support Tiers</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`${theme.subtext}`}>Bronze Supporter</span>
              <span className={`font-medium ${theme.text}`}>0.01 ETH / $5</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`${theme.subtext}`}>Silver Supporter</span>
              <span className={`font-medium ${theme.text}`}>0.1 ETH / $50</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`${theme.subtext}`}>Gold Supporter</span>
              <span className={`font-medium ${theme.text}`}>1 ETH / $100</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TipJar;